import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import './OnboardWizard.css';

// Import step components
import StartStep from './onboard/StartStep';
import CaptureStep from './onboard/CaptureStep';
import ConfigureStep from './onboard/ConfigureStep';
import ConfirmStep from './add-channel/ConfirmStep';
import CommitStep from './onboard/CommitStep';

// Define wizard steps
type WizardStep = 'start' | 'capture' | 'configure' | 'confirm' | 'commit';

interface WizardState {
  currentStep: WizardStep;
  protocol: 'a2a' | 'ap2' | 'mcp' | null;
  capturedPayload: any;
  derivedSchema: any;
  channelConfig: any;
}

const OnboardWizardPage: React.FC = () => {
  const navigate = useNavigate();
  const { actions } = useApp();
  
  const [wizardState, setWizardState] = useState<WizardState>({
    currentStep: 'start',
    protocol: null,
    capturedPayload: null,
    derivedSchema: null,
    channelConfig: null,
  });

  const steps: { key: WizardStep; label: string }[] = [
    { key: 'start', label: 'Start' },
    { key: 'capture', label: 'Capture' },
    { key: 'configure', label: 'Configure' },
    { key: 'confirm', label: 'Confirm' },
    { key: 'commit', label: 'Complete' },
  ];

  const currentStepIndex = steps.findIndex(s => s.key === wizardState.currentStep);

  const goToStep = (step: WizardStep) => {
    setWizardState(prev => ({ ...prev, currentStep: step }));
  };

  const nextStep = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      goToStep(steps[nextIndex].key);
    }
  };

  const prevStep = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      goToStep(steps[prevIndex].key);
    }
  };

  const handleProtocolSelect = (protocol: 'a2a' | 'ap2' | 'mcp') => {
    setWizardState(prev => ({ ...prev, protocol }));
    nextStep();
  };

  const handlePayloadCaptured = (payload: any, derivedSchema: any) => {
    setWizardState(prev => ({ ...prev, capturedPayload: payload, derivedSchema }));
    nextStep();
  };

  const handleConfigurationComplete = (config: any, formState?: any) => {
    // formState is optional and not used in onboard wizard, but accepted for compatibility
    setWizardState(prev => ({ ...prev, channelConfig: config }));
    nextStep();
  };

  const handleChannelCreated = (config: any) => {
    // Channel already added to app state by ConfirmStep
    setWizardState(prev => ({ ...prev, channelConfig: config }));
    nextStep();
  };

  const handleFinish = () => {
    // Navigate to channels page after completion
    navigate('/channels');
  };

  const handleConfigureChannel = () => {
    // Navigate to channel edit page
    if (wizardState.channelConfig?.config_id) {
      navigate(`/channels/${wizardState.channelConfig.config_id}`);
    }
  };

  // Render current step
  const renderStep = () => {
    switch (wizardState.currentStep) {
      case 'start':
        return <StartStep onProtocolSelect={handleProtocolSelect} />;
      case 'capture':
        return (
          <CaptureStep
            protocol={wizardState.protocol!}
            onPayloadCaptured={handlePayloadCaptured}
            onBack={prevStep}
          />
        );
      case 'configure':
        return (
          <ConfigureStep
            protocol={wizardState.protocol!}
            capturedPayload={wizardState.capturedPayload}
            derivedSchema={wizardState.derivedSchema}
            onConfigurationComplete={handleConfigurationComplete}
            onBack={prevStep}
          />
        );
      case 'confirm':
        return (
          <ConfirmStep
            protocol={wizardState.protocol!}
            channelConfig={wizardState.channelConfig}
            mpxPublishConfig={null}
            onChannelCreated={handleChannelCreated}
            onBack={prevStep}
          />
        );
      case 'commit':
        return (
          <CommitStep
            channelConfig={wizardState.channelConfig}
            onFinish={handleFinish}
            onBack={prevStep}
            onConfigureChannel={handleConfigureChannel}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h3 mb-0 text-gray-800">
          <i className="fas fa-magic mr-2"></i>
          Channel Onboarding Wizard
        </h1>
        <button
          className="btn btn-sm btn-secondary"
          onClick={() => navigate('/channels')}
        >
          <i className="fas fa-times mr-1"></i>
          Cancel
        </button>
      </div>

      {/* Progress Bar */}
      <div className="card shadow mb-4">
        <div className="card-body">
          <div className="wizard-progress">
            <div className="wizard-steps">
              {steps.map((step, index) => (
                <div
                  key={step.key}
                  className={`wizard-step ${
                    index === currentStepIndex
                      ? 'active'
                      : index < currentStepIndex
                      ? 'completed'
                      : ''
                  }`}
                >
                  <div className="wizard-step-circle">
                    {index < currentStepIndex ? (
                      <i className="fas fa-check"></i>
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>
                  <div className="wizard-step-label">{step.label}</div>
                  {index < steps.length - 1 && <div className="wizard-step-line"></div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="wizard-content">
        {renderStep()}
      </div>
    </div>
  );
};

export default OnboardWizardPage;
